import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ProjectAndCustomer } from '../project/projectType';
import EstimateApi from './estimateApi';
import { Estimate, ItemizedEstimate, EstimateTotal } from './estimateType';
import ProjectApi from '../project/projectApi';
import HorizontalMenu from '../components/common/horizontalMenu';
import { formatAddress, formatCurrency } from '../lib/global_function';
import Breadcrumbs from '../components/common/breadcrumbs';
import { Button } from '../components/ui/button';
import { Alert } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Trash, AlertTriangle, Package, User, Briefcase } from 'lucide-react';

const DeleteEstimatePage: React.FC = () => {
  const breadcrumbs = [
    { label: 'Project', path: '/main/project' },
    { label: 'Estimate', path: '/main/project/estimate' },
    { label: 'Delete', path: '/main/project/estimate/delete' }
  ];

  const location = useLocation();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const estimate: Estimate | undefined = location.state?.estimate;
  const [projectsAndCustomers, setProjectsAndCustomers] = useState<ProjectAndCustomer[]>([]);
  const [itemizedEstimates, setItemizedEstimates] = useState<ItemizedEstimate[]>([]);
  const [totals, setTotals] = useState<EstimateTotal | null>(null);

  // Check if estimate is available
  useEffect(() => {
    if (!estimate) {
      setErrorMessage('Estimate not available, please click the Go To Estimate button to select an estimate.');
    }
  }, [estimate]);

  useEffect(() => {
    const fetchProjectsAndCustomers = async () => {
      try {
        const fetchedProjectsAndCustomers = await ProjectApi.getProjectsAndCustomers();
        setProjectsAndCustomers(fetchedProjectsAndCustomers);
      } catch (error) {
        setErrorMessage(error instanceof Error ? error.message : 'An unknown error occurred.');
      }
    };

    const fetchItemizedEstimates = async () => {
      if (!estimate) return;

      try {
        const response = await EstimateApi.getItemizedEstimates(estimate.estimate_id);
        setItemizedEstimates(response.itemizedEstimates);
        setTotals(response.totals);
      } catch (error) {
        setErrorMessage(error instanceof Error ? error.message : 'An unknown error occurred.');
      }
    };

    fetchProjectsAndCustomers();
    if (estimate) {
      fetchItemizedEstimates();
    }
  }, [estimate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!estimate) return;

    setErrorMessage('');
    setMessage('');

    try {
      await EstimateApi.deleteEstimate(estimate.estimate_id);
      setMessage('Estimate deleted successfully');
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'An unknown error occurred.');
    }
  };

  const handleNavigateToEstimateList = () => {
    navigate('/main/project/estimate');
  };

  const projectAndCustomer = estimate ? projectsAndCustomers.find(
    (pc) => pc.project_id === estimate.project_id && pc.customer_id === estimate.customer_id
  ) : undefined;

  const formattedAddress = projectAndCustomer ? formatAddress(
    projectAndCustomer.customer_address_1,
    '',
    projectAndCustomer.customer_city,
    projectAndCustomer.customer_state,
    projectAndCustomer.customer_zip
  ) : '';

  return (
    <>
      <HorizontalMenu />
      <div className="container-safe py-6">
        <Breadcrumbs items={breadcrumbs} />

        <div className="mt-6">
          <h1 className="text-2xl font-semibold text-foreground mb-6">Delete Estimate</h1>

          {/* Error Message */}
          {errorMessage && (
            <Alert variant="error" className="mb-6">
              <div> {errorMessage}
                
              </div>
              <div>
              {!estimate && (
                <Button onClick={handleNavigateToEstimateList} size="medium">
                  Go to Estimate
                </Button>
              )}
              </div>
            </Alert>
          )}

          {/* Success Message */}
          {message ? (
            <div className="space-y-4">
              <Alert variant="success">
                {message}
              </Alert>
              <Button onClick={handleNavigateToEstimateList} size="medium">
                Return to Estimates
              </Button>
            </div>
          ) : estimate ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Main Content - 2 columns */}
              <div className="lg:col-span-2 space-y-6">
                {/* Project and Customer Information */}
                <div className="bg-white rounded-2xl border-2 border-muted p-6 shadow-card">
                  <h2 className="text-xl font-semibold text-foreground mb-4">Project & Customer</h2>
                  
                  <div className="space-y-4">
                    {/* Project Info */}
                    <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <Briefcase className="size-5 text-blue-600 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-foreground mb-1">Project</p>
                        <p className="text-sm text-paragraph">
                          <span className="font-semibold">{projectAndCustomer?.name}</span>
                          {projectAndCustomer?.project_description && (
                            <> - {projectAndCustomer.project_description.substring(0, 100)}</>
                          )}
                          {projectAndCustomer?.project_type && (
                            <Badge variant="secondary" className="ml-2">{projectAndCustomer.project_type}</Badge>
                          )}
                        </p>
                      </div>
                    </div>

                    {/* Customer Info */}
                    <div className="flex items-start gap-3 p-4 bg-green-50 rounded-lg border border-green-200">
                      <User className="size-5 text-green-600 mt-0.5 shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-foreground mb-1">Customer</p>
                        <p className="text-sm text-paragraph">
                          <span className="font-semibold">
                            {projectAndCustomer?.customer_first_name} {projectAndCustomer?.customer_last_name}
                          </span>
                          <br />
                          {projectAndCustomer?.customer_phone && <>{projectAndCustomer.customer_phone} • </>}
                          {projectAndCustomer?.customer_email && <>{projectAndCustomer.customer_email}</>}
                          {formattedAddress && (
                            <>
                              <br />
                              {formattedAddress}
                            </>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                  </div>

                {/* Estimate Details */}
                <div className="bg-white rounded-2xl border-2 border-muted p-6 shadow-card">
                  <h2 className="text-xl font-semibold text-foreground mb-4">Estimate Details</h2>
                  
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm font-medium text-paragraph mb-1">Estimate Name</p>
                      <p className="text-base text-foreground font-semibold">{estimate.name}</p>
                    </div>

                    {estimate.description && (
                      <div>
                        <p className="text-sm font-medium text-paragraph mb-1">Description</p>
                        <p className="text-base text-foreground">{estimate.description}</p>
                      </div>
                    )}

                    {(estimate.time_to_complete_day > 0 || estimate.time_to_complete_hr > 0) && (
                      <div>
                        <p className="text-sm font-medium text-paragraph mb-1">Estimated Time to Complete</p>
                        <p className="text-base text-foreground">
                          {estimate.time_to_complete_day > 0 && (
                            <>
                              {Number(estimate.time_to_complete_day).toFixed(0)} Days
                              {estimate.time_to_complete_hr > 0 && " and "}
                            </>
                          )}
                          {estimate.time_to_complete_hr > 0 && (
                            <>{Number(estimate.time_to_complete_hr).toFixed(0)} Hours</>
                          )}
                        </p>
                      </div>
                    )}

                    {estimate.terms_and_conditions && (
                      <div>
                        <p className="text-sm font-medium text-paragraph mb-1">Terms and Conditions</p>
                        <p className="text-sm text-foreground whitespace-pre-wrap bg-gray-50 p-3 rounded-lg border border-gray-200">
                          {estimate.terms_and_conditions}
                        </p>
                      </div>
                    )}
                  </div>
                  </div>

                {/* Line Items */}
                {itemizedEstimates.length > 0 && (
                  <div className="bg-white rounded-2xl border-2 border-muted p-6 shadow-card">
                    <h2 className="text-xl font-semibold text-foreground mb-4">Line Items</h2>
                    
                    <div className="space-y-3">
                      {itemizedEstimates.map((item) => (
                        <div
                          key={item.itemized_estimate_id}
                          className="border-2 border-muted rounded-xl p-4"
                        >
                          {/* Description */}
                          <div className="mb-3">
                            <p className="text-sm font-medium text-paragraph mb-1">
                              {item.code && <span className="text-primary">{item.code} - </span>}
                              Description
                            </p>
                            <p className="text-base text-foreground">{item.description}</p>
                          </div>

                          {/* Details Grid */}
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm bg-gray-50 p-3 rounded-lg">
                            <div>
                              <span className="text-paragraph font-medium">Unit Price:</span>
                              <span className="ml-2 text-foreground font-semibold">
                                ${formatCurrency(Number(item.unit_price_dollar).toFixed(2))}
                              </span>
                            </div>
                            <div>
                              <span className="text-paragraph font-medium">Quantity:</span>
                              <span className="ml-2 text-foreground font-semibold">{item.quantity}</span>
                            </div>
                            <div>
                              <span className="text-paragraph font-medium">Subtotal:</span>
                              <span className="ml-2 text-foreground font-semibold">
                                ${formatCurrency(Number(item.sub_total_dollar).toFixed(2))}
                              </span>
                            </div>
                            <div>
                              <span className="text-paragraph font-medium">Cost Type:</span>
                              <span className="ml-2 text-foreground">{item.cost_type}</span>
                            </div>
                            {item.duration_hour > 0 && (
                              <div>
                                <span className="text-paragraph font-medium">Duration:</span>
                                <span className="ml-2 text-foreground">
                                  {formatCurrency(Number(item.duration_hour).toFixed(1))} hrs
                                </span>
                              </div>
                            )}
                            <div>
                              <span className="text-paragraph font-medium">Optional:</span>
                              {item.is_optional ? (
                                <Badge variant="warning" className="ml-2">Yes</Badge>
                              ) : (
                                <span className="ml-2 text-foreground">No</span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  )}

                {/* Warning and Delete Button */}
                <div className="bg-white rounded-2xl border-2 border-destructive/30 p-6 shadow-card">
                  <div className="flex items-start gap-3 mb-6">
                    <AlertTriangle className="size-6 text-destructive shrink-0 mt-0.5" />
                    <div>
                      <h3 className="text-lg font-semibold text-destructive mb-2">Warning</h3>
                      <p className="text-sm text-paragraph">
                        If you delete this estimate, all associated itemized estimates will be permanently deleted as well. 
                        This action cannot be undone.
                      </p>
                    </div>
                  </div>

                  <form onSubmit={handleSubmit}>
                    <Button 
                      type="submit" 
                      size="medium"
                    >
                      <Trash className="size-5" />
                      Delete Estimate
                    </Button>
                  </form>
                </div>
                </div>

              {/* Estimate Summary - 1 column */}
              {totals && (
                <div className="lg:col-span-1">
                  <div className="sticky top-6 p-6 bg-gradient-to-br from-primary/5 to-primary/10 border-2 border-primary/30 rounded-lg shadow-sm">
                    <h3 className="text-lg font-semibold text-foreground mb-4">Estimate Summary</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-700">Subtotal:</span>
                        <span className="font-semibold">
                          ${formatCurrency(Number(totals.total_dollar || 0).toFixed(2))}
                        </span>
                      </div>
                      {Number(totals.tax_rate ?? 0) > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-700">Tax ({totals.tax_rate}%):</span>
                          <span className="font-semibold">
                            ${formatCurrency(Number(totals.tax_amount || 0).toFixed(2))}
                          </span>
                        </div>
                      )}
                      {Number(totals.discount_usd ?? 0) > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-700">Discount:</span>
                          <span className="font-semibold text-red-600">
                            -${formatCurrency(Number(totals.discount_usd || 0).toFixed(2))}
                          </span>
                        </div>
                      )}
                      <div className="pt-3 border-t-2 border-primary/30">
                        <div className="flex justify-between items-center">
                          <span className="text-lg font-bold text-foreground">Total:</span>
                          <span className="text-2xl font-bold text-primary">
                            ${formatCurrency(Number(totals.final_total || 0).toFixed(2))}
                          </span>
                        </div>
                      </div>
                      {Number(totals.optional_dollar ?? 0) > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-300 text-xs text-gray-600">
                          <div className="flex justify-between mb-1">
                            <span>Required items:</span>
                            <span>${formatCurrency(Number(totals.non_optional_dollar || 0).toFixed(2))}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Optional items:</span>
                            <span>${formatCurrency(Number(totals.optional_dollar || 0).toFixed(2))}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div >
              
              <Alert variant='info'>No estimate data available to display.</Alert>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default DeleteEstimatePage;